import { Component, Inject, OnDestroy, OnInit } from '@angular/core';
import { MAT_LEGACY_DIALOG_DATA as MAT_DIALOG_DATA, MatLegacyDialogRef as MatDialogRef } from '@angular/material/legacy-dialog';
import { FormBuilder, Validators } from '@angular/forms';
import { Subscription } from 'rxjs';
import { ServerDataService } from '@core/services/server-data/server-data.service';
import { VerificationTimerService } from '@core/services/verification-timer/verification-timer.service';
import { SessionStorageService } from '@core/services/session-storage/session-storage.service';
import { STORAGE_NAMES } from '@root/app.config';
import { ShareService } from '@core/services/share-data/share-data.service';

@Component({
	selector: 'app-verify-email-modal',
	templateUrl: './verify-email-modal.component.html',
	styleUrls: ['./verify-email-modal.component.scss', './verify-email-modal.responsive.scss'],
})
export class VerifyEmailModalComponent implements OnInit, OnDestroy {
	static readonly ID = crypto.randomUUID();

	readonly CODE_LENGTH = 6;
	readonly CODE_REG_EXP = /^\d{6}$/;
	submitted = false;
	verifying = false;
	sending = false;
	form = this.fb.group({
		code: ['', [Validators.required, Validators.pattern(this.CODE_REG_EXP)]],
	});

	timeLeft: number;
	timerLeftSubscription?: Subscription;

	constructor(
		private readonly fb: FormBuilder,
		private readonly serverDataService: ServerDataService,
		private readonly verificationTimer: VerificationTimerService,
		private readonly dialogRef: MatDialogRef<VerifyEmailModalComponent>,
		private readonly sessionStorageService: SessionStorageService,
		private readonly shareService: ShareService,
		@Inject(MAT_DIALOG_DATA) public modalData: { email: string },
	) {}

	ngOnInit() {
		this.verificationTimer.startTimer();
		this.timerLeftSubscription = this.verificationTimer.timeLeftObservable.subscribe((timeLeft) => (this.timeLeft = timeLeft));
	}

	ngOnDestroy() {
		this.timerLeftSubscription.unsubscribe();
	}

	get f() {
		return this.form.controls;
	}

	close(isVerified = false): void {
		if (isVerified) {
			this.verificationTimer.stopTimer();
		}

		this.dialogRef.close(isVerified);
	}

	onSubmit(): void {
		this.submitted = true;

		if (this.form.valid) {
			this.verifying = true;
			this.dialogRef.disableClose = true;

			this.serverDataService.PostVerifyCode({ email: this.modalData.email, code: this.f.code.value }).subscribe({
				next: (res) => {
					if (res.success) {
						this.getPrice();
					} else {
						this.close(res.success);
					}
				},
				complete: () => {
					this.verifying = false;
					this.dialogRef.disableClose = false;
				},
				error: (err) => {
					console.error(err);
					this.verifying = false;
					this.dialogRef.disableClose = false;
					this.f.code.setErrors({ incorrect: true });
				},
			});
		}
	}

	onResendCode(): void {
		this.sending = true;

		this.serverDataService.PostSendVerificationCode({ email: this.modalData.email }).subscribe({
			complete: () => {
				this.sending = false;
				this.verificationTimer.startTimer();
			},
			error: (err) => console.error(err),
		});
	}

	allowDigitsOnly(event: KeyboardEvent) {
		const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Home', 'End', 'Enter'];

		if (allowedKeys.includes(event.key)) {
			this.resetErrorsWhenCodeCleared();
			return;
		}

		if (event.ctrlKey || event.metaKey || ((event.ctrlKey || event.metaKey) && ['c', 'v', 'x', 'a'].includes(event.key.toLowerCase()))) {
			this.resetErrorsWhenCodeCleared();
			return;
		}

		if (!/^[0-9]$/.test(event.key) && this.f.code.value.length < this.CODE_LENGTH) {
			event.preventDefault();
			this.setWrongFormatError(true);
			return;
		}

		this.setWrongFormatError(false);
	}

	allowPasteDigitsOnly(event: ClipboardEvent): void {
		const pastedText = event.clipboardData?.getData('text/plain');
		const allowStatus = this.CODE_REG_EXP.test(pastedText);
		if (!allowStatus) {
			event.preventDefault();
		}
		this.setWrongFormatError(!allowStatus);
	}

	setWrongFormatError(status: boolean): void {
		this.f.code.setErrors({ wrongFormat: status || null });
		this.f.code.updateValueAndValidity();
	}

	resetErrorsWhenCodeCleared(): void {
		if (!this.f.code.value) {
			this.setWrongFormatError(false);
			this.f.code.setErrors({ incorrect: null });
		}
	}

	getPrice(): void {
		const sessionKey = this.sessionStorageService.getSession(STORAGE_NAMES.zip_blind_config)?.session_key;
		this.serverDataService.getPrice(sessionKey).subscribe({
			next: (priceList) => {
				this.shareService.setPriceDetails(priceList);
				this.close(true);
			},
			error: (err) => {
				console.error(err);
			},
		});
	}
}
